import React from 'react';
import { useFieldHelper } from 'framework/ui/field';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import { Box } from '@mui/material';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import { DEFAULT_ERROR_MESSAGE } from 'framework/form';
import { useShowContext } from 'framework/show';
import { EmployeeLink } from 'domain/shared/employee';
import { AssignEmployeeProps, AssigneeOption, AssignEmployeeCallbacks } from './types';
import { Button } from 'framework/ui/mui-builder';
import { DropdownMenu } from 'framework/ui/menu';
import { useDashboardFieldsContext } from '../context/DashboardFieldsContext';

const isUserInEmployeesList = (employees: AssigneeOption[], currentUserAlias?: string): boolean => {
  return employees.some(emp => emp.alias === currentUserAlias);
};

const AssignMenu = ({
  employees,
  callbacks,
  currentUserAlias,
  entityIdSource = 'retentionRiskId',
  assigneeSource = 'retentionRiskAssigneeAlias',
}: {
  employees: AssigneeOption[];
  callbacks: AssignEmployeeCallbacks;
  currentUserAlias?: string;
  entityIdSource?: string;
  assigneeSource?: string;
}) => {
  const { addMessage } = useMessageService();
  const { getValue } = useFieldHelper({ source: assigneeSource });

  const assignAlias = getValue();
  const { data, setData } = useShowContext();
  const entityData = data.getData();
  const entityId = entityData[entityIdSource];

  const assignCallback = async (employeeAlias: string | null) => {
    try {
      await callbacks.onAssign(entityId, employeeAlias);
      setData?.({
        ...entityData,
        [assigneeSource]: employeeAlias,
      });
    } catch {
      addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
    }
  };

  const isAssignedToCurrentUser = isUserInEmployeesList(employees, currentUserAlias);

  return (
    <DropdownMenu
      menuPlacement="bottom-end"
      popupId="AssingMenu"
      sxList={{
        maxHeight: '150px',
        overflow: 'auto',
        p: '6px 8px',
      }}
      disablePortal={false}
      iconButton={
        <IconButton sx={{ p: 0 }}>
          <KeyboardArrowDownIcon sx={{ width: '20px', height: '20px' }} />
        </IconButton>
      }
    >
      {[
        {
          name: 'Unassign',
          alias: null,
        },
        ...(isAssignedToCurrentUser
          ? [
              {
                name: 'Assign to me',
                alias: currentUserAlias!,
              },
            ]
          : []),
        ...employees.filter(item => item.alias !== currentUserAlias && item.alias !== null),
      ].map(item => {
        return (
          <MenuItem
            key={item.alias}
            disabled={assignAlias === item.alias || (item.alias === null && !assignAlias)}
            onClick={() => {
              assignCallback(item.alias);
            }}
            sx={{
              fontSize: '12px',
              p: '7px !important',
              borderBottom: '1px solid var(--mui-palette-TableCell-border)',
            }}
          >
            {item.name}
          </MenuItem>
        );
      })}
    </DropdownMenu>
  );
};

interface AssingEmployeeWithContextProps extends Omit<
  AssignEmployeeProps,
  'employees' | 'callbacks' | 'currentUserAlias'
> {
  employees?: AssignEmployeeProps['employees'];
  callbacks?: AssignEmployeeProps['callbacks'];
  currentUserAlias?: string;
}

const AssignEmployee = ({
  employees: propEmployees,
  callbacks: propCallbacks,
  currentUserAlias: propCurrentUserAlias,
  entityIdSource,
  assigneeSource = 'retentionRiskAssigneeAlias',
  renderAssignee,
  renderAssignButton,
}: AssingEmployeeWithContextProps) => {
  const context = useDashboardFieldsContext();
  const { addMessage } = useMessageService();
  const { data, setData } = useShowContext();
  const { getValue } = useFieldHelper({ source: assigneeSource });
  const assignAlias = getValue();

  const employees = propEmployees || context?.assigneeOptions || [];
  const callbacks = propCallbacks || context?.assignCallbacks;
  const currentUserAlias = propCurrentUserAlias || context?.currentUserAlias;
  const entityData = data.getData();
  const entityId = entityData[entityIdSource || 'retentionRiskId'];

  if (!callbacks) {
    return null;
  }

  const isAssignedToCurrentUser = isUserInEmployeesList(employees, currentUserAlias);

  const assignToMeCallback = async () => {
    try {
      await callbacks.onAssign(entityId, currentUserAlias!);
      setData?.({
        ...entityData,
        [assigneeSource]: currentUserAlias,
      });
    } catch {
      addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
    }
  };

  const defaultRenderAssignee = (assigneeAlias: string) => {
    const employee = employees.find(emp => emp.alias === assigneeAlias);
    const employeeName = employee?.name || assigneeAlias;
    return <EmployeeLink id={assigneeAlias} name={employeeName} isOpenInNewTab />;
  };

  const defaultRenderAssignButton = () => (
    <Button
      onClick={assignToMeCallback}
      sx={{
        padding: '2px 10px',
        fontSize: '9px',
      }}
      color="primary"
    >
      Assign to me
    </Button>
  );

  const assigneeComponent = renderAssignee ? renderAssignee(assignAlias) : defaultRenderAssignee(assignAlias);
  const assignButtonComponent = renderAssignButton ? renderAssignButton() : defaultRenderAssignButton();

  if (assignAlias) {
    return (
      <Box display="flex" alignItems="center">
        {assigneeComponent}
        <AssignMenu
          employees={employees}
          callbacks={callbacks}
          currentUserAlias={currentUserAlias}
          entityIdSource={entityIdSource}
          assigneeSource={assigneeSource}
        />
      </Box>
    );
  }

  if (isAssignedToCurrentUser) {
    return assignButtonComponent;
  }

  return (
    <Box display="flex" alignItems="center">
      <Box sx={{ fontSize: '12px', color: 'neutral.main' }}>Unassigned</Box>
      <AssignMenu
        employees={employees}
        callbacks={callbacks}
        currentUserAlias={currentUserAlias}
        entityIdSource={entityIdSource}
        assigneeSource={assigneeSource}
      />
    </Box>
  );
};

export default AssignEmployee;
